/**
 * Structured diff of one drifted property's baseline and current values.
 *
 * TCM stores most structured values as JSON text (`"[\"a\",\"b\"]"`), and the
 * drift detail used to print both sides whole, so one user added to a 40-entry
 * exclusion list read as two walls of text. This parses both sides and names
 * each difference: entries added to or removed from a list (order ignored),
 * fields that changed, and lists whose only change is order.
 *
 * Pure; the renderer is `components/Drift/DriftValueDiff.tsx`.
 */

/** Everything that differs at one place inside the property. */
export interface ValueDiffGroup {
  /** Where inside the property, outermost first. Empty means the property itself. */
  path: string[]
  /** List entries present now and not in the baseline. */
  added: unknown[]
  /** List entries in the baseline and gone now. */
  removed: unknown[]
  /** A changed value. `undefined` on one side means the field is new or gone. */
  change: { from: unknown; to: unknown } | null
  /** The list holds the same entries in a different order. */
  reordered: boolean
}

/** Nesting past this compares the rest whole rather than recursing. */
const MAX_DEPTH = 12

/**
 * Fields that name an object in a list, tried in order. A field counts only when
 * every entry on both sides carries a unique scalar value for it.
 */
const IDENTITY_FIELDS = ['id', 'Id', 'identity', 'Identity', 'name', 'Name', 'displayName', 'DisplayName']

/** Parse a JSON object or array held as text; anything else passes through. */
export function parseDriftValue(value: unknown): unknown {
  if (typeof value !== 'string') return value
  const text = value.trim()
  const looksStructured =
    (text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))
  if (!looksStructured) return value
  try {
    return JSON.parse(text)
  } catch {
    return value
  }
}

type Obj = Record<string, unknown>

function isObj(value: unknown): value is Obj {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isScalar(value: unknown): boolean {
  return value === null || (typeof value !== 'object' && typeof value !== 'function')
}

/** A canonical string for equality: objects with sorted keys. */
function stableKey(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableKey).join(',')}]`
  if (isObj(value)) {
    return `{${Object.keys(value)
      .sort()
      .map(k => `${JSON.stringify(k)}:${stableKey(value[k])}`)
      .join(',')}}`
  }
  return value === undefined ? 'undefined' : JSON.stringify(value)
}

/** An absent side of a list or object reads as an empty one, so its entries read as added or removed. */
function emptyLike(value: unknown): unknown {
  if (Array.isArray(value)) return []
  if (isObj(value)) return {}
  return undefined
}

/** Compare a baseline and a current value; an empty result means they read the same. */
export function diffDriftValues(expected: unknown, actual: unknown): ValueDiffGroup[] {
  const groups = new Map<string, ValueDiffGroup>()
  walk([], parseDriftValue(expected), parseDriftValue(actual), groups, 0)
  return [...groups.values()]
}

/**
 * True when the diff adds nothing over showing both values: they read the same,
 * or the only difference is one plain value (a word, a number) swapped for another.
 */
export function isPlainValueChange(groups: ValueDiffGroup[]): boolean {
  if (groups.length !== 1) return false
  const [g] = groups
  return (
    g.path.length === 0 &&
    g.added.length === 0 &&
    g.removed.length === 0 &&
    !g.reordered &&
    g.change !== null &&
    isScalar(g.change.from ?? null) &&
    isScalar(g.change.to ?? null)
  )
}

function group(groups: Map<string, ValueDiffGroup>, path: string[]): ValueDiffGroup {
  const key = JSON.stringify(path)
  let g = groups.get(key)
  if (!g) {
    g = { path, added: [], removed: [], change: null, reordered: false }
    groups.set(key, g)
  }
  return g
}

function isEmptyContainer(value: unknown): boolean {
  return (Array.isArray(value) && value.length === 0) || (isObj(value) && Object.keys(value).length === 0)
}

function walk(path: string[], a: unknown, b: unknown, groups: Map<string, ValueDiffGroup>, depth: number): void {
  // Not set against an empty list is a real change; coercing would read the two as equal.
  if ((a == null) !== (b == null) && isEmptyContainer(a ?? b)) {
    group(groups, path).change = { from: a, to: b }
    return
  }
  if (a == null && b != null) a = emptyLike(b) ?? a
  if (b == null && a != null) b = emptyLike(a) ?? b

  if (depth < MAX_DEPTH && Array.isArray(a) && Array.isArray(b)) {
    diffLists(path, a, b, groups, depth)
    return
  }
  if (depth < MAX_DEPTH && isObj(a) && isObj(b)) {
    const keys = [...Object.keys(a), ...Object.keys(b).filter(k => !(k in a))]
    for (const k of keys) {
      const from = k in a ? a[k] : undefined
      const to = k in b ? b[k] : undefined
      if (isScalar(from ?? null) && isScalar(to ?? null)) {
        if (stableKey(from) !== stableKey(to)) group(groups, [...path, k]).change = { from, to }
      } else {
        walk([...path, k], from, to, groups, depth + 1)
      }
    }
    return
  }
  if (stableKey(a) !== stableKey(b)) group(groups, path).change = { from: a, to: b }
}

function identityField(a: unknown[], b: unknown[]): string | null {
  const all = [...a, ...b]
  if (all.length === 0 || !all.every(isObj)) return null
  for (const field of IDENTITY_FIELDS) {
    const ok = [a, b].every(side => {
      const seen = new Set<string>()
      return (side as Obj[]).every(item => {
        const v = item[field]
        if (v == null || !isScalar(v)) return false
        const k = stableKey(v)
        if (seen.has(k)) return false
        seen.add(k)
        return true
      })
    })
    if (ok) return field
  }
  return null
}

function diffLists(path: string[], a: unknown[], b: unknown[], groups: Map<string, ValueDiffGroup>, depth: number): void {
  const field = identityField(a, b)
  const keyOf = field ? (item: unknown) => stableKey((item as Obj)[field]) : stableKey

  // Pair entries by key; a plain list can repeat an entry, so match counts.
  const pool = new Map<string, unknown[]>()
  for (const item of a) {
    const k = keyOf(item)
    pool.set(k, [...(pool.get(k) ?? []), item])
  }
  const added: unknown[] = []
  const matched: Array<[unknown, unknown]> = []
  for (const item of b) {
    const k = keyOf(item)
    const candidates = pool.get(k)
    if (candidates && candidates.length > 0) {
      matched.push([candidates.shift(), item])
    } else {
      added.push(item)
    }
  }
  const removed = [...pool.values()].flat()

  // One object swapped for another reads better as its changed fields.
  if (added.length === 1 && removed.length === 1 && isObj(added[0]) && isObj(removed[0])) {
    walk(path, removed[0], added[0], groups, depth + 1)
  } else if (added.length > 0 || removed.length > 0) {
    const g = group(groups, path)
    g.added.push(...added)
    g.removed.push(...removed)
  }

  if (field) {
    for (const [before, after] of matched) {
      walk([...path, String((after as Obj)[field])], before, after, groups, depth + 1)
    }
  }

  if (added.length === 0 && removed.length === 0) {
    const order = (list: unknown[]) => list.map(keyOf).join('\u0000')
    if (order(a) !== order(b)) group(groups, path).reordered = true
  }
}

/** A value as it reads in a tag: compact JSON for structures, "(none)" for nothing. */
export function formatDiffLeaf(value: unknown): string {
  if (value === undefined || value === null || value === '') return '(none)'
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}
